/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createSignal } from 'davaux/client'
import type { StyleProps } from './types.js'
import { pickStyleProps } from './types.js'

/** Props for the CopyButton island component. */
export interface CopyButtonProps extends StyleProps {
  /** Text content written to the clipboard when the button is clicked. */
  value: string
  /** Milliseconds to show the copied state before resetting to the default label. */
  timeout?: number
  /** Button label shown in the default (not-yet-copied) state. */
  label?: string
  /** Button label shown immediately after a successful copy. */
  copiedLabel?: string
  /** Additional CSS class names to apply. */
  class?: string
  [key: string]: unknown
}

function CopyButton({
  value,
  timeout = 2000,
  label = 'Copy',
  copiedLabel = 'Copied',
  class: className,
  ...rest
}: CopyButtonProps) {
  const [copied, setCopied] = createSignal(false)
  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)
  const style = Object.keys(styleObj).length
    ? Object.entries(styleObj)
        .map(([k, v]) => `${k}:${v}`)
        .join(';')
    : undefined

  function handleCopy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), timeout)
    })
  }

  return (
    <button
      type='button'
      class={`dv-copy-button${className ? ` ${className}` : ''}`}
      style={style}
      data-copied={() => copied() || undefined}
      onClick={handleCopy}
      {...ownRest}
    >
      {() => (copied() ? copiedLabel : label)}
    </button>
  )
}

export default reactive(CopyButton)
